import {Component, forwardRef, Input} from "@angular/core";
import {AbstractControl, FormArray, FormBuilder, NG_VALUE_ACCESSOR} from "@angular/forms";
import {DropdownOptionModel} from "../../../models/components/form-controls/dropdown-option.model";
import {DictionaryModel} from "../../../models/dictionary.model";
import {FormControlBaseComponent} from "../form-control-base/form-control-base.component";

@Component({
    selector: "pcg-ng-multi-dropdown",
    template: require('./multi-dropdown.component.html'),
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultiDropdownComponent), multi: true},
    ]
})
export class MultiDropdownComponent extends FormControlBaseComponent<DictionaryModel<string>> {
    public formArray: FormArray;

    private isControlsCreated: boolean = false;

    @Input()
    public propertyName: string;

    @Input()
    public availableOptions: Array<DropdownOptionModel> = new Array<DropdownOptionModel>();

    public constructor(
        private formBuilder: FormBuilder
    ) {
        super(/*DEFAULT_VALUE*/{});
        this.formArray = this.formBuilder.array([]);
    }

    public writeValue(value: any): void {
        super.writeValue(value);
        if (!this.isControlsCreated) {
            this.buildControlsByValue(this.controlValue);
            this.isControlsCreated = true;
        }
    }

    public getAvailableOptions(currentFieldIndex: number): Array<DropdownOptionModel> {
        // this.controlValue can't be used because user may leave empty text => control value contains only fields with text
        const selectedOptions = (this.formArray.value as Array<MultiDropdownFieldValue>)
            .filter((x, i) => i !== currentFieldIndex)
            .map(x => x.alias);

        const availableOptions = this.availableOptions
            .filter(option => selectedOptions.indexOf(option.value) < 0);

        return availableOptions;
    }

    public canAddField(): boolean {
        return this.formArray.controls.length < this.availableOptions.length;
    }

    public addField(): void {
        const initialValue = new MultiDropdownFieldValue(this.formArray.length, "", "");
        const control = this.createFieldControlByValue(initialValue);
        this.formArray.push(control);
    }

    public removeField(index: number): void {
        this.formArray.removeAt(index);

        const currentFormArrayValue = this.formArray.value as Array<MultiDropdownFieldValue>;
        const newControlValue = this.mapArrayValueToObjectValue(currentFormArrayValue);
        this.writeNewValue(newControlValue);

        if(!this.formArray.controls.length) {
            this.addField();
        }
    }

    public clear(): void {
        while (this.formArray.length !== 0) {
            this.formArray.removeAt(0);
        }
        this.addField();
    }

    private buildControlsByValue(currentValue: DictionaryModel<string>): void {
        if (currentValue) {
            const aliases = Object.keys(currentValue);
            if (aliases.length) {
                aliases
                    .map((alias, index) => {
                        const fieldValue = new MultiDropdownFieldValue(index, alias, currentValue[alias]);
                        return this.createFieldControlByValue(fieldValue);
                    })
                    .forEach(control => this.formArray.push(control));
            } else {
                this.addField(); // to not force user to click
            }
        }
    }

    private createFieldControlByValue(fieldValue: MultiDropdownFieldValue): AbstractControl {
        const groupFormControl = this.formBuilder.group({
            id: [fieldValue.id],
            alias: [fieldValue.alias],
            text: [fieldValue.text]
        });

        groupFormControl.valueChanges.subscribe((changedValue: MultiDropdownFieldValue) => {
            const currentFormArrayValue = this.formArray.value as Array<MultiDropdownFieldValue>;
            const changedValueFromArray = currentFormArrayValue.find(x => x.id === changedValue.id);
            if (!changedValueFromArray) {
                currentFormArrayValue.push(changedValue);
            } else {
                Object.assign(changedValueFromArray, changedValue);
            }

            const newControlValue = this.mapArrayValueToObjectValue(currentFormArrayValue);
            this.writeNewValue(newControlValue);
        });

        return groupFormControl;
    }

    private mapArrayValueToObjectValue(arrayValue: Array<MultiDropdownFieldValue>): any {
        const objectValue = arrayValue.reduce((accumulator, currentValue) => {
            if (currentValue.alias && currentValue.text) {
                accumulator[currentValue.alias] = currentValue.text;
            }
            return accumulator;
        }, {});

        return objectValue;
    }
}

// tslint:disable-next-line:max-classes-per-file
class MultiDropdownFieldValue {
    public constructor(
        public id: number,
        public alias: string,
        public text: string
    ) {
    }
}
